import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { postService } from '@services/api/posts'
import SuccessModal from '@components/ui/SuccessModal'
import { CATEGORIES } from '@constants/categories'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import { isInFreeTrial, getFreeTrialDaysLeft } from '@utils/freeTrial'

function CreateBannerPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    link: '',
    bannerImage: null,
    bannerFile: null
  })

  // const categories = [
  //   'Toutes les catégories',
  //   'Auto - Moto',
  //   'Immobilier',
  //   'Informatique',
  //   'Téléphones',
  //   'Vêtements',
  //   'Loisirs',
  //   'Services',
  //   'Décorations',
  //   'Modes',
  //   'Bijoux',
  //   'Sacs',
  //   'Chaussures',
  //   'Chemises',
  //   'Autres'
  // ]

  const categories = CATEGORIES

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('image/')) {
      setFormData({ 
        ...formData, 
        bannerImage: URL.createObjectURL(file),
        bannerFile: file 
      })
    } else {
      toast.error('Veuillez sélectionner une image valide')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.bannerFile) {
      toast.error('Veuillez sélectionner une image de bannière')
      return
    }

    if (!formData.title || !formData.category || !formData.link || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    setIsSubmitting(true)

    try {
      const clientId = user?.client_id || user?.id
      if (!clientId) {
        toast.error('Vous devez être connecté pour publier une bannière')
        setIsSubmitting(false)
        return
      }

      const formDataToSend = new FormData()
      formDataToSend.append('libele', formData.title)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('siteWeb', formData.link)
      formDataToSend.append('post_client_id', clientId)
      formDataToSend.append('type', '3')
      formDataToSend.append('files[]', formData.bannerFile)

      const response = await postService.createPost(formDataToSend)
      const postId = response.data?.post_id || response.data?.id || response.post_id

      if (isInFreeTrial(user)) {
        // Période d'essai : le backend auto-approuve pendant createPost
        const daysLeft = getFreeTrialDaysLeft(user)
        toast.success(`✅ Bannière publiée ! Période d'essai — ${daysLeft} jour${daysLeft > 1 ? 's' : ''} restant${daysLeft > 1 ? 's' : ''}.`)
        setTimeout(() => navigate('/dashboard'), 1500)
      } else {
        // Hors période d'essai : rediriger vers le paiement
        navigate('/payment-banner', {
          state: {
            postId: postId,
            initialDuration: 7,
            postData: {
              title: formData.title,
              category: formData.category,
              link: formData.link,
              description: formData.description
            }
          }
        })
      }
      
    } catch (error) {
      console.error('Erreur lors de la création de la bannière:', error)
      const msg = error.response?.data?.message || error.message || 'Erreur lors de la publication de la bannière'
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    navigate('/ligdi')
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  return (
    <div className="min-h-screen bg-[#fefcf8] overflow-x-hidden">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-2 sm:px-4 py-3 sm:py-8">
        <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-6 md:p-8 lg:p-12 overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#001F3F] mb-8">Publier une bannière publicitaire</h1>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Colonne gauche */}
              <div className="space-y-6">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Titre<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Titre de votre bannière"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Catégorie<span className="text-red-600">*</span>
                  </label>
                  <div className="w-full overflow-hidden">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full max-w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        backgroundSize: '1.5em 1.5em',
                        boxSizing: 'border-box'
                      }}
                      required
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      {categories.filter(cat => cat !== 'Toutes les catégories').map((cat, index) => (
                        <option key={index} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Lien de destination */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Lien de destination<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Décrivez votre bannière publicitaire"
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                    required
                  ></textarea>
                </div>
              </div>

              {/* Colonne droite */}
              <div className="space-y-6">
                {/* Upload bannière */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Image de la bannière<span className="text-red-600">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Dimensions recommandées: 1200x300 pixels. Formats acceptés: JPG, PNG, WebP
                  </p>
                  
                  <label className="block w-full border-2 border-dashed border-gray-300 rounded-lg overflow-hidden cursor-pointer hover:border-[#D4AF37] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      required
                    />
                    {formData.bannerImage ? (
                      <div className="relative">
                        <img src={formData.bannerImage} alt="Bannière" className="w-full h-auto" />
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <p className="text-white text-sm font-semibold">Changer l'image</p>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-[4/1] flex flex-col items-center justify-center p-8">
                        <svg className="w-16 h-16 text-gray-400 mb-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                        </svg>
                        <p className="text-sm font-semibold text-[#001F3F] mb-1">Cliquez pour télécharger une image</p>
                        <p className="text-xs text-gray-500">ou glissez-déposez votre fichier ici</p>
                      </div>
                    )}
                  </label>
                </div>

                {/* Récapitulatif de la publication */}
                <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#FFD700]/5 border-2 border-[#D4AF37]/30 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-[#001F3F] mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    Durée de publication
                  </h3>
                  <div className="bg-white/80 rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Durée d'affichage :</span>
                      <span className="text-lg font-bold text-[#D4AF37]">
                        À définir au paiement
                      </span>
                    </div>
                  </div>
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>✓ Affichage sur la page d'accueil</li>
                    <li>✓ Visible par tous les utilisateurs</li>
                    <li>✓ Suivi des clics et statistiques</li>
                    <li>✓ Rapport de performance détaillé</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bouton Publier */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-20 py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#c09d2a] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Publication en cours...
                  </span>
                ) : (
                  'Publier'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de succès */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="localhost:3000 indique"
        message="Votre bannière a été publiée avec succès !"
      />
    </div>
  )
}

export default CreateBannerPage
